import { memo, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { selectOptionChainColumns } from '../../../../selectors/home'
import { selectShowOptionChainQuantityInLots } from '../../../../selectors/common'
import { OIChange, OutlookToName } from '../../../typings/options.typings'
import { Stack, Typography } from '@mui/material'
import ArrowUp from '../../../../assets/icons/ArrowUp'
import ArrowDown from '../../../../assets/icons/ArrowDown'
import { formatLargeNumber } from '../../../../util/screener'
import { Outlook, outlookToJSON } from '../../../../proto/generated/candle-data-depot-option-chain'
import { Security } from '../../../../proto/generated/security'
import { getSecurityKey } from '../../../../util/security'
import {
    selectCandleChangeField,
    selectCandleChangeIv,
    selectCandleChangeLtp,
    selectCandleChangeLtpChangePercent,
    selectCandleChangeOpenInterest,
    selectCandleChangeOpenInterestChange,
} from '../../../../selectors/security'
import { calculateOptionOutlook } from '../../../../util/options'
import ROUTES from '../../../../assets/routes'
import { matchPath } from 'react-router-dom'
import { OptionChainViewKey } from '../../../../assets/constants'
import { green, red } from "../../../../assets/colors";
import { useTheme } from "@mui/material/styles";
import { RenderAsk, RenderBid } from './BidAskRender'

export const greekColumns = ['delta', 'gamma', 'vega', 'theta', 'rho']

export const formatLtpChangePercent = (num: string) => {
    const numb = Math.abs(parseFloat(num))
    if (numb < 10) {
        return numb?.toFixed(2).padStart(4, '0') + '%'
    }
    if (numb < 100) {
        return numb?.toFixed(1).padStart(4, '0') + '%'
    }
    return numb?.toFixed(1) + '%'
}

export const formatGreeks = (num: number) => {
    if (num === 0) return '-'
    if (Math.abs(num) >= 100) return num?.toFixed(0) ?? '-'
    if (Math.abs(num) >= 10) return num?.toFixed(1) ?? '-'
    if (Math.abs(num) >= 0) return num?.toFixed(2) ?? '-'
}

const DynamicColumnText = ({ obj, index, subColumn, maxOiChange, maxPricePercentChange, type }: {
    obj: any,
    index: number,
    subColumn?: string,
    maxPricePercentChange: number,
    maxOiChange: number,
    type: 'normal' | 'extended'
}) => {
    const secKey = getSecurityKey(obj?.security?.exchange ?? 0, obj?.security?.code ?? '')
    const fullSecurity = useMemo(() => obj.security, [obj])
    const selectedColumns = useSelector(selectOptionChainColumns(matchPath(ROUTES.simulator, window.location.pathname) ? OptionChainViewKey.HISTORICAL : OptionChainViewKey.LIVE, type))
    const candleChangeValue = useSelector(selectCandleChangeField(secKey, selectedColumns[index]))
    const format = ['openInterest', 'volume'].includes(selectedColumns[index])
    const quantityInLots: boolean = useSelector(selectShowOptionChainQuantityInLots)

    let opacity = 0.8

    const returnValue = useMemo(() => {
        let returnValuefinal: any = candleChangeValue ?? obj?.[selectedColumns[index]] ?? '-'
        if (greekColumns.includes(selectedColumns[index])) {
            if(!candleChangeValue && !!obj?.optionGreeks?.[selectedColumns[index]]) {
                if(selectedColumns[index] === 'gamma') returnValuefinal = obj?.optionGreeks?.[selectedColumns[index]]?.toFixed(4)??'-'
                else returnValuefinal = formatGreeks(obj?.optionGreeks?.[selectedColumns[index]]) ?? '-'
            } else {
                returnValuefinal = candleChangeValue ?? '-'
            }
        }
        if (selectedColumns[index] === 'volume') {
            if (quantityInLots && returnValuefinal !== '-') returnValuefinal = (Number(returnValuefinal) / (fullSecurity.lotSize ?? 50))
        }

        if (format && returnValuefinal !== '-') {
            returnValuefinal = formatLargeNumber(Number(returnValuefinal))
        }
        if (Number(returnValuefinal) === 0) returnValuefinal = '-'
        return returnValuefinal
    }, [candleChangeValue, format, fullSecurity.lotSize, index, quantityInLots, selectedColumns])

    if (selectedColumns[index] === 'openInterest') {
        return <RenderOpenInterest returnValue={obj?.[selectedColumns[index]]} security={obj?.security} />
    } else if (selectedColumns[index] === 'oiChange|outlook') {
        const changeData: OIChange = obj?.oiChanges
        let oiChangeValue: any = changeData?.oiChange?.change ?? '-'
        if (oiChangeValue !== '-' && maxOiChange > 0 && subColumn === 'outlook') {
            opacity *= Math.abs(Number(oiChangeValue)) / maxOiChange
        }
        if (oiChangeValue !== '-' && maxPricePercentChange > 0 && obj?.['ltpChangePercent'] && subColumn === 'outlook') {
            opacity *= Math.abs(obj?.['ltpChangePercent']) / maxPricePercentChange
        }
        if (quantityInLots && oiChangeValue !== '-') {
            oiChangeValue = (Number(oiChangeValue) / (fullSecurity.lotSize ?? 50))
        }
        if (oiChangeValue !== '-') {
            oiChangeValue = formatLargeNumber(Number(oiChangeValue))
        }
        if (subColumn === 'oiChange') {
            return <RenderOIChange oiChangeInitial={changeData?.oiChange?.change} security={obj?.security} />
        }
        return <RenderOutlook oiChangeInitial={changeData?.oiChange?.change} security={obj?.security}
            maxOiChange={maxOiChange} intialLtpChangePercent={obj?.['ltpChangePercent']} />
    }

    if (selectedColumns[index] === 'ltp') {
        return <RenderLtpCell ltpOnly={type === 'extended'} initialLtp={obj?.[selectedColumns[index]]}
            intialLtpChangePercent={obj?.['ltpChangePercent']} security={obj?.security} />
    } else if (selectedColumns[index] === 'impliedVolatility') {
        return <RenderImpliedVolatilityCell initialIv={obj?.[selectedColumns[index]]}
            security={obj?.security} />
    }

    else if (selectedColumns[index] === 'bid') {
        return (
            <RenderBid
                security={obj?.security}
                strike={obj?.security?.strikePrice}
                initialValue={obj?.["topBid"]}
                type={'call'}
                handleOrderClick={obj?.handleOrderClick}
                rowKey={obj?.security?.code ?? ''}
                illiquid={obj?.illiquid}
            />
        )
    }
    else if (selectedColumns[index] === 'ask') {
        return (
            <RenderAsk
                security={obj?.security}
                strike={obj?.security?.strikePrice}
                initialValue={obj?.["topAsk"]}
                type={'call'}
                handleOrderClick={obj?.handleOrderClick}
                rowKey={obj?.security?.code ?? ''}
                illiquid={obj?.illiquid}
            />
        )
    }

    return (
        <div className={
            [OutlookToName.LONG_BUILDUP, OutlookToName.SHORT_COVERING].some((x) => String(returnValue).includes(x)) ?
                'green-outlook' :
                String(returnValue).includes(OutlookToName.LONG_UNWINDING) ?
                    'orange-outlook' :
                    String(returnValue).includes(OutlookToName.SHORT_BUILDUP) ?
                        'red-outlook' :
                        (selectedColumns[index] === 'openInterest' || (selectedColumns[index] === 'oiChange|outlook' && subColumn === 'oiChange')) ? 'oi-bar-inline z-10' : 'relative z-10'}
            style={{
                opacity: opacity + 0.2,
                pointerEvents: 'none',
                // minWidth:
                //     selectedColumns[index] === 'strike' ? '110px' :
                //         ['bid', 'ask'].includes(selectedColumns[index]) ? '80px' :
                //             ['openInterest', 'oiChange|outlook'].includes(selectedColumns[index]) ? '120px' :
                //                 greekColumns.includes(selectedColumns[index]) ? '80px' :
                //                     '80px',
            }}>
            {
                returnValue
            }
        </div>
    )
}

export const RenderImpliedVolatilityCell = memo(({ initialIv, security }: {
    initialIv: string,
    security: Security
}) => {
    const secKey = getSecurityKey(security?.exchange, security?.code)
    const iv = useSelector(selectCandleChangeIv(secKey))
    const opacity = 0.8
    const initialRenderedIv = useRef(false)
    const ivFinal = useMemo(() => {
        if (iv === undefined && !initialRenderedIv.current) {
            return Number(initialIv)
        } else {
            initialRenderedIv.current = true
            return (iv ?? 0)
        }
    }, [initialIv, iv])
    return (
        <div className={'relative z-10'}
            style={{
                opacity: opacity + 0.2,
                pointerEvents: 'none'
            }}>
            {isNaN(ivFinal) ? '-' : ivFinal.toFixed(2)}
        </div>
    )
})

export const RenderLtpCell = memo(({ initialLtp, intialLtpChangePercent, security, ltpOnly }: {
    initialLtp: string,
    intialLtpChangePercent: string,
    security: Security
    ltpOnly: boolean
}) => {
    const secKey = getSecurityKey(security?.exchange, security?.code)
    const ltp = useSelector(selectCandleChangeLtp(secKey))
    const ltpChangePercent = useSelector(selectCandleChangeLtpChangePercent(secKey))
    const opacity = 0.8
    const initialRenderedLtp = useRef(false)
    const initialRenderedLtpChange = useRef(false)

    const ltpFinal = useMemo(() => {
        if (ltp === undefined && !initialRenderedLtp.current) {
            return Number(initialLtp) / (security.priceMultiplier ?? 100)
        } else {
            initialRenderedLtp.current = true
            return (ltp ?? 0) / (security.priceMultiplier ?? 100)
        }
    }, [security.priceMultiplier, initialLtp, ltp])

    const ltpChangePercentFinal = useMemo(() => {
        if (ltpChangePercent === undefined && !initialRenderedLtpChange.current) {
            return Number(intialLtpChangePercent)
        } else {
            initialRenderedLtpChange.current = true
            return Number(ltpChangePercent?.toFixed(2)) ?? 0
        }
    }, [intialLtpChangePercent, ltpChangePercent])

    return (
        <Stack direction={"row-reverse"} gap={0.5}>
            {
                !ltpOnly ?
                    <>
                        <Typography variant={"caption"} color={"text.secondary"}>{(ltpChangePercentFinal===0 && ltpFinal===0) ? '-' : formatLtpChangePercent(ltpChangePercentFinal.toString() ?? '')}</Typography>
                        <Typography color={ltpChangePercentFinal > 0 ? green : (ltpChangePercentFinal < 0 ? red : 'transparent')} style={{
                            fontSize: '12px'
                        }}> {ltpChangePercentFinal > 0 ? <ArrowUp sx={{ fontSize: '10px' }} /> : <ArrowDown sx={{ fontSize: '10px' }} />}</Typography>
                    </> : null
            }
            <Typography variant={"body2"}>{ltpFinal===0 ? '' : ltpFinal.toFixed(2)}</Typography>
        </Stack>
    )
})

export const RenderOpenInterest = memo(({ returnValue: initialOi, security }: {
    returnValue: string,
    security: Security
}) => {
    const theme = useTheme();
    const secKey = getSecurityKey(security?.exchange, security?.code)
    const openInterest = useSelector(selectCandleChangeOpenInterest(secKey))
    const quantityInLots: boolean = useSelector(selectShowOptionChainQuantityInLots)
    const initialRendered = useRef(false)

    const oiFinal = useMemo(() => {
        if (openInterest === undefined && !initialRendered.current) {
            return Number(initialOi)
        } else {
            initialRendered.current = true
            return (openInterest ?? 0)
        }
    }, [initialOi, openInterest])

    return (
        <div className={'oi-bar-inline z-10'}
            style={{
                textShadow: `
      -0.75px -0.75px 0 ${theme.palette.background.paper},
       0.75px -0.75px 0 ${theme.palette.background.paper},
      -0.75px  0.75px 0 ${theme.palette.background.paper},
       0.75px  0.75px 0 ${theme.palette.background.paper}
    `,
                fontWeight: 500,
                pointerEvents: 'none'
            }}>
            {
                oiFinal ? formatLargeNumber(quantityInLots ? oiFinal / (security.lotSize ?? 1) : oiFinal) : '-'
            }
        </div>
    )
})

const RenderOIChange = ({ oiChangeInitial, security }: { oiChangeInitial: number, security: Security }) => {
    const theme = useTheme();
    const secKey = getSecurityKey(security?.exchange, security?.code)
    const oiChange = useSelector(selectCandleChangeOpenInterestChange(secKey))
    const quantityInLots: boolean = useSelector(selectShowOptionChainQuantityInLots)
    const initialRendered = useRef(false)

    const oiChangeFinal = useMemo(() => {
        let res = 0
        if (oiChange === undefined && !initialRendered.current) {
            res = Number(oiChangeInitial)
        } else {
            initialRendered.current = true
            res = (oiChange ?? 0)
        }
        return quantityInLots ? res / (security.lotSize ?? 50) : res
    }, [security.lotSize, oiChange, oiChangeInitial, quantityInLots])

    return (
        <div className={'oi-bar-inline z-10'}
            style={{
                textShadow: `
      -0.75px -0.75px 0 ${theme.palette.background.paper},
       0.75px -0.75px 0 ${theme.palette.background.paper},
      -0.75px  0.75px 0 ${theme.palette.background.paper},
       0.75px  0.75px 0 ${theme.palette.background.paper}
    `,
                fontWeight: 500,
                pointerEvents: 'none'
            }}>
            {
                oiChangeFinal === 0 ? '-' : formatLargeNumber(Math.abs(oiChangeFinal))
            }
        </div>
    )
}

const RenderOutlook = ({ oiChangeInitial, security, intialLtpChangePercent, maxOiChange }: {
    oiChangeInitial: number,
    intialLtpChangePercent: string,
    security: Security,
    maxOiChange: number
}) => {
    const secKey = getSecurityKey(security?.exchange, security?.code)
    const oiChange = useSelector(selectCandleChangeOpenInterestChange(secKey))
    const ltpChangePercent = useSelector(selectCandleChangeLtpChangePercent(secKey))
    const initialRendered = useRef(false)
    const initialRenderedLtpChange = useRef(false)

    const oiChangeFinal = useMemo(() => {
        let res = 0
        if (oiChange === undefined && !initialRendered.current) {
            res = Number(oiChangeInitial)
        } else {
            initialRendered.current = true
            res = (oiChange ?? 0)
        }
        return res
    }, [oiChange, oiChangeInitial])

    const ltpChangePercentFinal = useMemo(() => {
        if (ltpChangePercent === undefined && !initialRenderedLtpChange.current) {
            return Number(intialLtpChangePercent)
        } else {
            initialRenderedLtpChange.current = true
            return Number(ltpChangePercent?.toFixed(2)) ?? 0
        }
    }, [intialLtpChangePercent, ltpChangePercent])

    const outlook = calculateOptionOutlook(ltpChangePercentFinal, oiChangeFinal)
    // const selectedTimeInterval = timeInterval
    // const changeData: OIChange = obj?.oiChanges.find((v: any) => areTimeBucketsSame(selectedTimeInterval, v.oiChange?.timeInterval))
    // let oiChangeValue: any = changeData?.oiChange?.change ?? '-'
    let opacity = 0.6
    if (maxOiChange > 0) {
        opacity += 0.4 * Math.abs(Number(oiChangeFinal)) / maxOiChange
    }
    // if (oiChangeValue !== '-' && maxPricePercentChange > 0 && obj?.['ltpChangePercent'] && subColumn === 'outlook') {
    //     opacity *= Math.abs(obj?.['ltpChangePercent']) / maxPricePercentChange
    // }
    // if (quantityInLots && oiChangeValue !== '-') {
    //     oiChangeValue = (Number(oiChangeValue) / (obj?.security?.lotSize ?? 50))
    // }
    // if (oiChangeValue !== '-') {
    //     oiChangeValue = formatLargeNumber(Number(oiChangeValue))
    // }
    let outlookValue = [Outlook.OUTLOOK_UNKNOWN, Outlook.UNRECOGNIZED, undefined].includes(outlook) ? '-' : OutlookToName[outlookToJSON(Number(outlook))]
    // returnValue = subColumn === 'oiChange' ? oiChangeValue : outlookValue
    return (
        <div className={
            [OutlookToName.LONG_BUILDUP, OutlookToName.SHORT_COVERING].some((x) => String(outlookValue).includes(x)) ?
                'green-outlook' :
                String(outlookValue).includes(OutlookToName.LONG_UNWINDING) ?
                    'orange-outlook' :
                    String(outlookValue).includes(OutlookToName.SHORT_BUILDUP) ?
                        'red-outlook' : ''
        }
            style={{
                opacity: opacity,
                pointerEvents: 'none'
            }}>
            {
                outlookValue
            }
        </div>
    )
}

export default memo(DynamicColumnText)